import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, input, signal, ViewEncapsulation } from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { s25Resource } from "../../../util/s25.resource";
import { S25LoadingInlineStaticComponent } from "../../s25-loading/s25.loading.inline.static.component";
import { activityLabel, humanizeSnakeCase, ENGAGEMENT_LABELS } from "./engagement.labels";
import {
    BadgeCriteriaProgress,
    BadgeProgressItem,
    EngagementBadge,
    EngagementBadgeEarned,
    EngagementService,
} from "./engagement.service";
import { S25FilestoreImageComponent } from "../../../standalone/s25.filestore.image.component";

type TierVisualMode = "prominent-earned" | "dimmed-earned" | "progress" | "locked";

export interface BadgeShowcaseTierViewModel {
    def: EngagementBadge;
    mode: TierVisualMode;
    progressPct: number;
    progressLabel: string;
    criteria: BadgeCriteriaProgress[];
}

export interface BadgeShowcaseGroupViewModel {
    groupKey: string;
    groupTitle: string;
    /** From progress tier, else first non-locked, else first tier. Rendered under the card title. */
    groupDescription?: string;
    tiers: BadgeShowcaseTierViewModel[];
}

function tierSortValue(t: number | undefined): number {
    return t ?? 0;
}

function groupKeyFor(def: EngagementBadge): string {
    const g = def.badgeGroup?.trim();
    return g && g.length > 0 ? g : def.badgeKey;
}

function progressTotals(item: BadgeProgressItem | undefined): { current: number; threshold: number } {
    if (!item?.criteria?.length) {
        return { current: 0, threshold: 0 };
    }
    let current = 0;
    let threshold = 0;
    for (const criterion of item.criteria) {
        current += Math.min(criterion.current, criterion.threshold);
        threshold += criterion.threshold;
    }
    return { current, threshold };
}

function buildGroupViewModels(
    defs: EngagementBadge[],
    earnedBadgeIds: Set<number>,
    progressById: Map<number, BadgeProgressItem>,
    showProgress: boolean,
): BadgeShowcaseGroupViewModel[] {
    const byGroup = new Map<string, EngagementBadge[]>();
    for (const d of defs) {
        if (!d.isActive) continue;
        const earned = d.badgeId != null && earnedBadgeIds.has(d.badgeId);
        if (!d.isVisible && !earned) continue;
        const key = groupKeyFor(d);
        let arr = byGroup.get(key);
        if (!arr) {
            arr = [];
            byGroup.set(key, arr);
        }
        arr.push(d);
    }

    const groups: BadgeShowcaseGroupViewModel[] = [];
    for (const [groupKey, groupDefs] of byGroup) {
        const sortedDefs = [...groupDefs].sort((a, b) => tierSortValue(a.tier) - tierSortValue(b.tier));
        const earnedInGroup = sortedDefs.filter((d) => d.badgeId != null && earnedBadgeIds.has(d.badgeId));
        const maxEarnedTier =
            earnedInGroup.length > 0
                ? Math.max(...earnedInGroup.map((d) => tierSortValue(d.tier)))
                : Number.NEGATIVE_INFINITY;

        const firstUnearnedIdx = sortedDefs.findIndex((d) => d.badgeId == null || !earnedBadgeIds.has(d.badgeId));

        const tiers: BadgeShowcaseTierViewModel[] = sortedDefs.map((def, idx) => {
            const earned = def.badgeId != null && earnedBadgeIds.has(def.badgeId);
            const tierNum = tierSortValue(def.tier);
            const progressItem = def.badgeId != null ? progressById.get(def.badgeId) : undefined;
            const { current, threshold } = progressTotals(progressItem);
            const criteria: BadgeCriteriaProgress[] = progressItem?.criteria?.length
                ? progressItem.criteria
                : (def.criteria ?? []).map((c) => ({
                      activityTypeKey: c.activityTypeKey,
                      current: earned ? c.thresholdCount : 0,
                      threshold: c.thresholdCount,
                  }));

            if (earned) {
                const mode: TierVisualMode = tierNum === maxEarnedTier ? "prominent-earned" : "dimmed-earned";
                const earnedTotal = criteria.reduce((sum, c) => sum + c.threshold, 0);
                return {
                    def,
                    mode,
                    progressPct: 100,
                    progressLabel: earnedTotal > 0 ? `${earnedTotal} / ${earnedTotal}` : "",
                    criteria,
                };
            }

            const isActiveTier = idx === firstUnearnedIdx;
            if (isActiveTier && showProgress && threshold > 0) {
                return {
                    def,
                    mode: "progress" as TierVisualMode,
                    progressPct: Math.min(100, (current / threshold) * 100),
                    progressLabel: `${current} / ${threshold}`,
                    criteria,
                };
            }

            return { def, mode: "locked" as TierVisualMode, progressPct: 0, progressLabel: "", criteria };
        });

        const descTier = tiers.find((t) => t.mode === "progress") ?? tiers.find((t) => t.mode !== "locked") ?? tiers[0];
        const groupDescription = descTier?.def.description?.trim() || undefined;

        const rawTitle =
            sortedDefs[0]?.badgeGroup?.trim() || sortedDefs[0]?.name || sortedDefs[0]?.badgeKey || groupKey;
        const title = isSnakeCase(rawTitle) ? humanizeSnakeCase(rawTitle) : rawTitle;

        groups.push({ groupKey, groupTitle: title, groupDescription, tiers });
    }

    groups.sort((a, b) => a.groupTitle.localeCompare(b.groupTitle));
    return groups;
}

function isSnakeCase(s: string): boolean {
    return /^[A-Z][A-Z0-9]*(_[A-Z0-9]+)+$/.test(s);
}

@TypeManagerDecorator("s25-ng-se-badge-showcase")
@Component({
    selector: "s25-ng-se-badge-showcase",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    imports: [CommonModule, S25LoadingInlineStaticComponent, S25FilestoreImageComponent],
    template: `
        <section class="se-badge-showcase">
            @if (!hideTitle()) {
                <h2 class="se-badge-showcase__title">{{ labels.badgeShowcaseTitle }}</h2>
            }

            @if (showcaseLoading()) {
                <s25-ng-loading-inline-static [text]="'Loading badges…'" />
            } @else if (defsRes.resource.error()) {
                <p class="se-badge-showcase__error">{{ defsRes.resource.error()!.message }}</p>
            } @else {
                <div class="se-badge-showcase__grid">
                    @for (g of groups(); track g.groupKey) {
                        <article class="se-badge-showcase__card">
                            <header class="se-badge-showcase__card-head">
                                <h3 class="se-badge-showcase__card-title">{{ g.groupTitle }}</h3>
                                @if (g.groupDescription) {
                                    <p class="se-badge-showcase__group-desc">{{ g.groupDescription }}</p>
                                }
                            </header>
                            <div class="se-badge-showcase__track" role="list">
                                @for (cell of g.tiers; track cell.def.badgeKey; let last = $last) {
                                    <div
                                        class="se-badge-showcase__tier"
                                        [class.se-badge-showcase__tier--prominent]="cell.mode === 'prominent-earned'"
                                        [class.se-badge-showcase__tier--dimmed]="cell.mode === 'dimmed-earned'"
                                        [class.se-badge-showcase__tier--progress]="cell.mode === 'progress'"
                                        [class.se-badge-showcase__tier--locked]="cell.mode === 'locked'"
                                        role="listitem"
                                    >
                                        <div class="se-badge-showcase__icon-wrap" [attr.title]="tierHint(cell)">
                                            @if (cell.def.iconUrl) {
                                                @if (cell.mode !== "locked") {
                                                    <s25-ng-filestore-image
                                                        class="se-badge-showcase__icon"
                                                        [class.se-badge-showcase__icon--dim]="
                                                            cell.mode === 'dimmed-earned'
                                                        "
                                                        [value]="cell.def.iconUrl"
                                                        [readOnly]="true"
                                                    />
                                                } @else {
                                                    <s25-ng-filestore-image
                                                        class="se-badge-showcase__icon se-badge-showcase__icon--locked-silhouette"
                                                        [value]="cell.def.iconUrl"
                                                        [readOnly]="true"
                                                    />
                                                    <span class="se-badge-showcase__lock" aria-hidden="true">🔒</span>
                                                }
                                            } @else {
                                                <span
                                                    class="se-badge-showcase__icon-fallback"
                                                    [class.se-badge-showcase__icon-fallback--locked]="
                                                        cell.mode === 'locked'
                                                    "
                                                >
                                                    {{ cell.def.name.slice(0, 1).toUpperCase() }}
                                                </span>
                                                @if (cell.mode === "locked") {
                                                    <span class="se-badge-showcase__lock" aria-hidden="true">🔒</span>
                                                }
                                            }
                                        </div>
                                        <div class="se-badge-showcase__name">{{ cell.def.name }}</div>
                                        @if (cell.mode === "progress") {
                                            <div class="se-badge-showcase__bar-wrap">
                                                <div
                                                    class="se-badge-showcase__bar"
                                                    [style.width.%]="cell.progressPct"
                                                ></div>
                                            </div>
                                        }
                                        @if (cell.mode !== "locked" && cell.criteria.length) {
                                            <button
                                                type="button"
                                                class="se-badge-showcase__detail-toggle"
                                                [attr.aria-expanded]="isExpanded(cell.def.badgeKey)"
                                                (click)="toggleExpanded(cell.def.badgeKey)"
                                            >
                                                @if (cell.progressLabel) {
                                                    {{ cell.progressLabel }}
                                                } @else {
                                                    Details
                                                }
                                                <span
                                                    class="se-badge-showcase__chevron"
                                                    [class.se-badge-showcase__chevron--open]="
                                                        isExpanded(cell.def.badgeKey)
                                                    "
                                                    >&#9654;</span
                                                >
                                            </button>
                                            @if (isExpanded(cell.def.badgeKey)) {
                                                <ul class="se-badge-showcase__criteria">
                                                    @for (c of cell.criteria; track c.activityTypeKey) {
                                                        <li class="se-badge-showcase__criteria-row">
                                                            <span>{{ getActivityLabel(c.activityTypeKey) }}</span>
                                                            <span class="se-badge-showcase__criteria-count">
                                                                {{ c.current }} / {{ c.threshold }}
                                                            </span>
                                                        </li>
                                                    }
                                                </ul>
                                            }
                                        }
                                        @if (cell.mode === "locked") {
                                            <div class="se-badge-showcase__muted">{{ labels.badgeLockedHint }}</div>
                                        }
                                    </div>
                                    @if (!last) {
                                        <span class="se-badge-showcase__arrow" aria-hidden="true">→</span>
                                    }
                                }
                            </div>
                        </article>
                    } @empty {
                        <p class="se-badge-showcase__empty">No visible badges configured.</p>
                    }
                </div>
            }
        </section>
    `,
    styles: [
        `
            :host {
                display: block;
            }
            .se-badge-showcase__title {
                margin: 0 0 1rem;
                font-size: 1.35rem;
            }
            .se-badge-showcase__grid {
                display: grid;
                grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
                gap: 1.25rem;
            }
            .se-badge-showcase__card {
                border: 1px solid var(--color--border);
                border-radius: 10px;
                padding: 1rem 1rem 1.15rem;
                background: var(--color--surface-bg);
                box-shadow: 0 1px 2px rgb(0 0 0 / 4%);
            }
            .se-badge-showcase__card-head {
                margin-bottom: 0.85rem;
            }
            .se-badge-showcase__card-title {
                margin: 0;
                font-size: 1.05rem;
            }
            .se-badge-showcase__track {
                display: flex;
                flex-wrap: wrap;
                align-items: flex-start;
                gap: 0.35rem 0.25rem;
            }
            .se-badge-showcase__group-desc {
                margin: 0.35rem 0 0;
                font-size: 0.82rem;
                color: var(--color--text-muted);
                line-height: 1.45;
            }
            .se-badge-showcase__tier {
                display: flex;
                flex-direction: column;
                align-items: center;
                text-align: center;
                min-width: 4.5rem;
                max-width: 7.5rem;
            }
            .se-badge-showcase__tier--prominent .se-badge-showcase__icon-wrap {
                width: 4.5rem;
                height: 4.5rem;
            }
            .se-badge-showcase__tier--dimmed .se-badge-showcase__icon-wrap {
                width: 2.75rem;
                height: 2.75rem;
                opacity: 0.55;
            }
            .se-badge-showcase__tier--progress .se-badge-showcase__icon-wrap {
                width: 3.35rem;
                height: 3.35rem;
            }
            .se-badge-showcase__tier--locked .se-badge-showcase__icon-wrap {
                width: 2.75rem;
                height: 2.75rem;
            }
            .se-badge-showcase__icon-wrap {
                position: relative;
                border-radius: 50%;
                overflow: hidden;
                border: 2px solid var(--color--border);
                background: var(--color--surface-bg);
                display: flex;
                align-items: center;
                justify-content: center;
            }
            .se-badge-showcase__tier--prominent .se-badge-showcase__icon-wrap {
                border-color: #b8860b;
                box-shadow: 0 2px 8px rgb(184 134 11 / 25%);
            }
            .se-badge-showcase__icon {
                width: 100%;
                height: 100%;
                display: block;
            }
            .se-badge-showcase__icon img {
                width: 100%;
                height: 100%;
                object-fit: cover;
                display: block;
                border: none;
                border-radius: 0;
            }
            .se-badge-showcase__icon--dim img {
                filter: grayscale(0.35);
            }
            .se-badge-showcase__icon--locked-silhouette img {
                filter: grayscale(1) brightness(1.15) contrast(0.85);
                opacity: 0.38;
            }
            .se-badge-showcase__icon-fallback {
                font-weight: 800;
                font-size: 1.25rem;
                color: var(--color--text-muted);
            }
            .se-badge-showcase__tier--prominent .se-badge-showcase__icon-fallback {
                font-size: 1.65rem;
            }
            .se-badge-showcase__icon-fallback--locked {
                color: #aaa;
                opacity: 0.5;
            }
            .se-badge-showcase__lock {
                position: absolute;
                inset: 0;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 1rem;
                text-shadow: 0 0 4px #fff;
            }
            .se-badge-showcase__name {
                margin-top: 0.4rem;
                font-size: 0.78rem;
                font-weight: 600;
                line-height: 1.2;
                color: var(--color--text-strong);
            }
            .se-badge-showcase__tier--dimmed .se-badge-showcase__name {
                font-size: 0.72rem;
                color: var(--color--text-muted);
            }
            .se-badge-showcase__bar-wrap {
                margin-top: 0.35rem;
                width: 100%;
                height: 6px;
                border-radius: 4px;
                background: var(--color--surface-2-bg);
                overflow: hidden;
            }
            .se-badge-showcase__bar {
                height: 100%;
                border-radius: 4px;
                background: linear-gradient(90deg, #5c6bc0, #3949ab);
                transition: width 0.25s ease;
            }
            .se-badge-showcase__detail-toggle {
                display: inline-flex;
                align-items: center;
                gap: 0.25rem;
                margin-top: 0.2rem;
                padding: 0;
                border: none;
                background: none;
                cursor: pointer;
                font-size: 0.7rem;
                color: var(--color--link-text);
                font-variant-numeric: tabular-nums;
            }
            .se-badge-showcase__detail-toggle:hover {
                text-decoration: underline;
            }
            .se-badge-showcase__chevron {
                font-size: 0.55rem;
                transition: transform 0.2s ease;
                color: var(--color--text-muted);
            }
            .se-badge-showcase__chevron--open {
                transform: rotate(90deg);
            }
            .se-badge-showcase__criteria {
                list-style: none;
                margin: 0.25rem 0 0;
                padding: 0;
                width: 100%;
                text-align: left;
                font-size: 0.68rem;
                color: var(--color--text);
            }
            .se-badge-showcase__criteria-row {
                display: flex;
                justify-content: space-between;
                gap: 0.3rem;
                padding: 0.1rem 0;
            }
            .se-badge-showcase__criteria-count {
                font-variant-numeric: tabular-nums;
                white-space: nowrap;
            }
            .se-badge-showcase__muted {
                margin-top: 0.25rem;
                font-size: 0.68rem;
                color: var(--color--text-muted);
                line-height: 1.2;
            }
            .se-badge-showcase__arrow {
                align-self: center;
                color: var(--color--text-muted);
                font-size: 1rem;
                padding: 0 0.1rem;
                user-select: none;
            }
            .se-badge-showcase__error {
                color: var(--color--danger);
            }
            .se-badge-showcase__empty {
                color: var(--color--text-muted);
                font-style: italic;
            }
        `,
    ],
})
export class SeBadgeShowcaseComponent {
    readonly labels = ENGAGEMENT_LABELS;

    readonly hideTitle = input(false);
    readonly contId = input<number | undefined>(undefined);
    readonly isMyData = computed(() => typeof this.contId() !== "number");

    readonly defsRes = s25Resource(() => EngagementService.getBadges());

    readonly myEarnedRes = s25Resource({
        if: () => this.isMyData(),
        loader: () => EngagementService.getMyBadges(),
    });

    readonly userEarnedRes = s25Resource({
        if: () => !this.isMyData(),
        params: () => this.contId()!,
        loader: ({ params }) => EngagementService.getUserBadges(params),
    });

    readonly myProgressRes = s25Resource({
        if: () => this.isMyData(),
        loader: () => EngagementService.getMyBadgeProgress(),
    });

    readonly earnedBadgeIds = computed(() => {
        const mine = this.myEarnedRes.value();
        const theirs = this.userEarnedRes.value();
        const list: EngagementBadgeEarned[] = this.isMyData() ? (mine ?? []) : (theirs ?? []);
        return new Set(list.map((e) => e.badgeId));
    });

    readonly progressById = computed(() => {
        const rows = this.myProgressRes.value() ?? [];
        return new Map(rows.map((r) => [r.badgeId, r]));
    });

    readonly groups = computed((): BadgeShowcaseGroupViewModel[] => {
        const defs = this.defsRes.value() ?? [];
        return buildGroupViewModels(defs, this.earnedBadgeIds(), this.progressById(), this.isMyData());
    });

    readonly showcaseLoading = computed(() => {
        if (this.defsRes.resource.isLoading()) {
            return true;
        }
        if (this.isMyData()) {
            return this.myEarnedRes.resource.isLoading();
        }
        return this.userEarnedRes.resource.isLoading();
    });

    private expandedKeys = signal(new Set<string>());

    isExpanded(badgeKey: string): boolean {
        return this.expandedKeys().has(badgeKey);
    }

    toggleExpanded(badgeKey: string) {
        const next = new Set(this.expandedKeys());
        if (next.has(badgeKey)) {
            next.delete(badgeKey);
        } else {
            next.add(badgeKey);
        }
        this.expandedKeys.set(next);
    }

    getActivityLabel = activityLabel;

    tierHint(cell: BadgeShowcaseTierViewModel): string {
        switch (cell.mode) {
            case "prominent-earned":
            case "dimmed-earned":
                return this.labels.badgeEarnedHint;
            case "progress":
                return cell.def.name;
            case "locked":
                return this.labels.badgeLockedHint;
            default:
                return cell.def.name;
        }
    }
}
